"use client";

/**
 * Select 컴포넌트
 * @description 고른 값을 보여주고 시트를 여는 필드. 앱 DS `Select` 의 웹 포팅. 네이티브
 *   `<select>` 가 아니다 — 목록은 바텀시트(`OptionSheet`)로 열고 이 자리는 **여는 버튼**만
 *   맡는다. 생김새는 `Input` 과 같은 토큰을 읽는다.
 * @example
 * <Select label="Country" value={country?.label} placeholder="Select" onOpen={openSheet} />
 */
import { ChevronDown } from "lucide-react";
import { memo, useId } from "react";

import { selectStyles } from "./Select.styles";
import type { SelectProps } from "./Select.types";

/** 시안의 필드 글리프 — `size.icon.md` */
const CHEVRON_SIZE = 24;

const Select = memo<SelectProps>(
  ({
    label,
    value,
    placeholder,
    onOpen,
    helper,
    error = false,
    disabled = false,
  }) => {
    const id = useId();
    const helperId = `${id}-helper`;
    const axis = { disabled, error };

    return (
      <div className={selectStyles.root}>
        {label ? (
          <span id={id} className={selectStyles.label(axis)}>
            {label}
          </span>
        ) : null}

        <button
          type="button"
          onClick={onOpen}
          disabled={disabled}
          aria-labelledby={label ? id : undefined}
          aria-describedby={helper ? helperId : undefined}
          className={selectStyles.trigger(axis)}
        >
          <span
            className={selectStyles.value({ filled: Boolean(value), disabled })}
          >
            {value || placeholder}
          </span>
          <ChevronDown
            aria-hidden
            size={CHEVRON_SIZE}
            className={selectStyles.chevron}
          />
        </button>

        {helper ? (
          <p id={helperId} className={selectStyles.helper(axis)}>
            {helper}
          </p>
        ) : null}
      </div>
    );
  },
);

Select.displayName = "Select";

export default Select;
